import { Link } from 'react-router';
import { InfoPage, InfoSection } from './InfoPage';

/**
 * What a badge means, and — mostly — what it does not.
 *
 * This page exists because the product shows verification badges and had
 * nowhere to explain them. There is no verification provider yet
 * ([ADR-0008](docs/adr/0008-auth-and-verification.md)), so every badge on this
 * deployment is a placeholder decision rather than a check anybody performed.
 * Saying otherwise here would be the exact failure the ADR is written to
 * prevent: a passenger reading "verified" as "somebody looked".
 *
 * When a provider exists, this page changes with it. Until then it errs
 * towards telling people the checks have not happened.
 */
export function VerificationScreen() {
  return (
    <InfoPage
      title="How verification works"
      standfirst="Short version: it does not, yet. Here is exactly what a badge on a profile means today, so nobody reads more into one than is there."
    >
      <InfoSection heading="No identity checks are running">
        <p className="m-0">
          OpenRoad does not yet check anybody's identity. There is no document check, no licence
          check, and no third party confirming that a member is who their profile says they are.
        </p>
        <p className="m-0">
          Any badge you see on this deployment was set when the account was created, not earned. If
          a profile shows an ID badge, that badge is a placeholder — treat it as though it were not
          there.
        </p>
      </InfoSection>

      <InfoSection heading="What a badge will mean">
        <p className="m-0">There are four kinds, and each will mean one narrow thing:</p>
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
          <li>
            <strong>Email</strong> — the address on the account received a message and somebody
            opened it. It says the address is real; it says nothing about the person.
          </li>
          <li>
            <strong>Phone</strong> — the same, for a number.
          </li>
          <li>
            <strong>ID</strong> — a government document was checked against the name on the account,
            by somebody whose job that is.
          </li>
          <li>
            <strong>Photo</strong> — the profile picture is of the person who holds the account.
          </li>
        </ul>
        <p className="m-0">
          None of them is a background check, and none of them will ever be one. A verified identity
          means we know who somebody is, not that they are safe to travel with.
        </p>
      </InfoSection>

      <InfoSection heading="What you can rely on instead">
        <p className="m-0">
          Ratings and reviews are real: they come from members who actually shared a ride, and only
          after it has happened. A driver's response rate is counted, not claimed. Both are on every
          profile.
        </p>
        <p className="m-0">
          Everything on the <Link to="/safety">safety page</Link> applies more, not less, while
          verification is missing.
        </p>
      </InfoSection>
    </InfoPage>
  );
}
